import React, { Component } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";
import {
  getRoomUnits,
  createRoomUnits,
  updateRoomUnit,
  deleteRoomUnit,
  addRoomOutOfService,
  removeRoomOutOfService,
  getRoomAvailability,
  getBranchList,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Btn, Chip, Toggle, IconBtn, Field, Dialog, DialogHead, DialogBtn, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import { canWrite, isBranchScopedAdmin, getUserBranchName, getSelectedBranchId } from "../../services/PermissionService";
import { hotelYmd } from "../../utils/period";

/**
 * Room stock — the numbered rooms each branch sells, by room type.
 *
 * Availability in the app is counted from these: active rooms of a type,
 * minus rooms out of service that night, minus bookings holding it. A type
 * with no rooms entered yet keeps selling its old tenant-wide "number of
 * rooms", so entering rooms is how a branch takes control of its stock.
 *
 * The hotel runs check-in/out in its own software; this page only keeps the
 * stock the app sells from. Out-of-service blocks are nights, both ends
 * included, in the branch's calendar.
 */

const CSS = `
.rs-form .form-control,.rs-form .custom-select{height:44px;padding:0 13px;background:${C.surface};
  border:1px solid ${C.border};border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.rs-form .form-control:focus{background:#fff;border-color:${C.sand};box-shadow:none}
.rs-row{display:grid;grid-template-columns:90px 90px minmax(0,1fr) 120px 112px;gap:12px;align-items:center;
  padding:11px 4px;border-bottom:1px solid ${C.panel}}
.rs-row:last-child{border-bottom:0}
`;

const fmtNight = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
};
const blockText = (b) => (b.from === b.to ? fmtNight(b.from) : `${fmtNight(b.from)} – ${fmtNight(b.to)}`);

class RoomUnits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      branches: [],
      branchId: isBranchScopedAdmin() ? null : getSelectedBranchId() || "",
      units: [],
      types: [], // room types sold at the branch, from the availability endpoint
      isLoaded: false,
      busy: false,
      // dialogs
      adding: null, // { roomTypeId, numbers, floor, notes }
      editing: null, // { _id, number, roomTypeId, floor, notes, isActive }
      blocking: null, // { unit, from, to, reason }
      removing: null, // unit
    };
  }

  componentDidMount() {
    if (isBranchScopedAdmin()) {
      this.load();
      return;
    }
    getBranchList().then((res) => {
      const branches = res && res.success && Array.isArray(res.data) ? res.data : [];
      const keep = branches.some((b) => String(b._id) === String(this.state.branchId));
      this.setState(
        { branches, branchId: keep ? this.state.branchId : branches[0] ? String(branches[0]._id) : "" },
        this.load
      );
    });
  }

  /** `branchId` for the API: a global admin names it, a branch admin's is implied. */
  scope = () => (this.state.branchId ? { branchId: this.state.branchId } : {});

  load = () => {
    if (!isBranchScopedAdmin() && !this.state.branchId) {
      this.setState({ isLoaded: true, units: [], types: [] });
      return;
    }
    this.setState({ isLoaded: false });
    Promise.all([getRoomUnits(this.scope()), getRoomAvailability({ ...this.scope(), days: 1 })]).then(([u, a]) => {
      if (!u || !u.success) toast.error((u && u.message) || "Could not load the rooms");
      this.setState({
        isLoaded: true,
        units: (u && u.success && u.data) || [],
        types: (a && a.success && a.data ? a.data : []).map((t) => ({ _id: String(t.roomTypeId), type: t.type, source: t.source })),
      });
    });
  };

  /** Run an API call, toast its message, reload on success. */
  run = (call, onDone) => {
    this.setState({ busy: true });
    call.then((res) => {
      this.setState({ busy: false });
      if (res && res.success) {
        toast.success(res.message);
        if (onDone) onDone();
        this.load();
      } else {
        toast.error((res && res.message) || "Something went wrong");
      }
    });
  };

  submitAdd = () => {
    const a = this.state.adding;
    if (!a.roomTypeId) return toast.error("Pick a room type sold at this branch");
    if (!a.numbers.trim()) return toast.error("Enter room numbers, e.g. 101 or 101-110");
    this.run(
      createRoomUnits({ ...this.scope(), roomTypeId: a.roomTypeId, numbers: a.numbers.trim(), floor: a.floor.trim(), notes: a.notes.trim() }),
      () => this.setState({ adding: null })
    );
  };

  submitEdit = () => {
    const e = this.state.editing;
    if (!e.number.trim()) return toast.error("Enter a room number");
    this.run(
      updateRoomUnit(e._id, { number: e.number.trim(), roomTypeId: e.roomTypeId, floor: e.floor.trim(), notes: e.notes.trim(), isActive: e.isActive }),
      () => this.setState({ editing: null })
    );
  };

  submitBlock = () => {
    const b = this.state.blocking;
    if (!b.from || !b.to) return toast.error("Pick the first and last night");
    if (b.to < b.from) return toast.error("The last night is before the first");
    this.run(
      addRoomOutOfService(b.unit._id, { from: b.from, to: b.to, reason: b.reason.trim() }),
      () => this.setState({ blocking: null })
    );
  };

  toggleActive = (unit) => this.run(updateRoomUnit(unit._id, { isActive: !unit.isActive }));

  /** Units grouped by room type, types in the branch's order, then any stragglers. */
  groups() {
    const { units, types } = this.state;
    const byType = new Map();
    units.forEach((u) => {
      const id = String((u.roomTypeId && u.roomTypeId._id) || u.roomTypeId || "");
      if (!byType.has(id)) byType.set(id, []);
      byType.get(id).push(u);
    });
    const out = types.map((t) => ({ id: t._id, type: t.type, source: t.source, units: byType.get(t._id) || [] }));
    byType.forEach((list, id) => {
      if (!types.some((t) => t._id === id)) {
        const name = (list[0].roomTypeId && list[0].roomTypeId.type) || "Room type no longer sold here";
        out.push({ id, type: name, source: "units", units: list, stray: true });
      }
    });
    return out;
  }

  renderUnitRow(u, write) {
    const today = hotelYmd(new Date());
    const blocks = (u.outOfService || []).filter((b) => b.to >= today);
    return (
      <div key={u._id} className="rs-row">
        <span style={{ fontSize: 16, fontWeight: 500, color: u.isActive ? C.ink : C.taupeLight }}>{u.number}</span>
        <span style={{ fontSize: 13, color: C.slate }}>{u.floor ? `Floor ${u.floor}` : "—"}</span>
        <div style={{ minWidth: 0 }}>
          {u.notes && <div style={{ fontSize: 13, color: C.slate }}>{u.notes}</div>}
          {blocks.length > 0 && (
            <div style={{ marginTop: u.notes ? 6 : 0, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {blocks.map((b) => (
                <span key={b._id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Chip tone="warn">
                    <span className="ms" style={{ fontSize: 15 }}>construction</span>
                    {blockText(b)}{b.reason ? ` · ${b.reason}` : ""}
                  </Chip>
                  {write && (
                    <button
                      type="button"
                      title="Remove this block"
                      onClick={() => this.run(removeRoomOutOfService(u._id, b._id))}
                      style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 0 }}
                    >
                      ✕
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {!u.notes && !blocks.length && <span style={{ fontSize: 13, color: C.taupeLight }}>—</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Toggle on={u.isActive} disabled={!write || this.state.busy} title={u.isActive ? "Stop selling this room" : "Sell this room again"}
            onClick={() => write && this.toggleActive(u)} />
          <span style={{ fontSize: 13, color: u.isActive ? C.green : C.taupeLight }}>{u.isActive ? "On sale" : "Off"}</span>
        </div>
        {write ? (
          <div style={{ display: "flex", gap: 6 }}>
            <IconBtn icon="event_busy" title="Take out of service" onClick={() => this.setState({ blocking: { unit: u, from: today, to: today, reason: "" } })} />
            <IconBtn icon="edit" title="Edit" onClick={() => this.setState({
              editing: {
                _id: u._id, number: u.number || "", floor: u.floor || "", notes: u.notes || "", isActive: !!u.isActive,
                roomTypeId: String((u.roomTypeId && u.roomTypeId._id) || u.roomTypeId || ""),
              },
            })} />
            <IconBtn icon="delete" tone="bad" title="Remove" onClick={() => this.setState({ removing: u })} />
          </div>
        ) : <span />}
      </div>
    );
  }

  renderDialogs() {
    const { adding, editing, blocking, removing, types, busy } = this.state;
    const typeSelect = (value, onChange) => (
      <Input type="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose a room type</option>
        {types.map((t) => <option key={t._id} value={t._id}>{t.type}</option>)}
      </Input>
    );
    const set = (key) => (patch) => this.setState((s) => ({ [key]: { ...s[key], ...patch } }));
    return (
      <React.Fragment>
        <Dialog isOpen={!!adding} toggle={() => !busy && this.setState({ adding: null })}>
          {adding && (
            <div className="rs-form">
              <DialogHead title="Add rooms" subtitle="One number, or several: 101-110, 201, 2A" onClose={() => this.setState({ adding: null })} />
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
                <Field label="Room type">{typeSelect(adding.roomTypeId, (v) => set("adding")({ roomTypeId: v }))}</Field>
                <Field label="Room numbers" hint="Ranges keep their zero padding (01-09). Up to 200 at once.">
                  <Input value={adding.numbers} onChange={(e) => set("adding")({ numbers: e.target.value })} placeholder="101-110, 201" />
                </Field>
                <div style={{ display: "grid", gridTemplateColumns: "120px minmax(0,1fr)", gap: 12 }}>
                  <Field label="Floor"><Input value={adding.floor} maxLength={20} onChange={(e) => set("adding")({ floor: e.target.value })} /></Field>
                  <Field label="Notes"><Input value={adding.notes} maxLength={200} onChange={(e) => set("adding")({ notes: e.target.value })} placeholder="Optional" /></Field>
                </div>
              </div>
              <div style={{ marginTop: 22, display: "flex", gap: 12 }}>
                <DialogBtn onClick={() => this.setState({ adding: null })} disabled={busy}>Cancel</DialogBtn>
                <DialogBtn kind="primary" onClick={this.submitAdd} disabled={busy}>{busy ? "Adding…" : "Add rooms"}</DialogBtn>
              </div>
            </div>
          )}
        </Dialog>

        <Dialog isOpen={!!editing} toggle={() => !busy && this.setState({ editing: null })}>
          {editing && (
            <div className="rs-form">
              <DialogHead title={`Room ${editing.number}`} onClose={() => this.setState({ editing: null })} />
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px", gap: 12 }}>
                  <Field label="Number"><Input value={editing.number} maxLength={20} onChange={(e) => set("editing")({ number: e.target.value })} /></Field>
                  <Field label="Floor"><Input value={editing.floor} maxLength={20} onChange={(e) => set("editing")({ floor: e.target.value })} /></Field>
                </div>
                <Field label="Room type" hint="Moving a room changes which type's stock it counts toward.">
                  {typeSelect(editing.roomTypeId, (v) => set("editing")({ roomTypeId: v }))}
                </Field>
                <Field label="Notes"><Input value={editing.notes} maxLength={200} onChange={(e) => set("editing")({ notes: e.target.value })} /></Field>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Toggle on={editing.isActive} onClick={() => set("editing")({ isActive: !editing.isActive })} />
                  <span style={{ fontSize: 14, color: C.ink }}>{editing.isActive ? "On sale" : "Not sold"}</span>
                </div>
              </div>
              <div style={{ marginTop: 22, display: "flex", gap: 12 }}>
                <DialogBtn onClick={() => this.setState({ editing: null })} disabled={busy}>Cancel</DialogBtn>
                <DialogBtn kind="primary" onClick={this.submitEdit} disabled={busy}>{busy ? "Saving…" : "Save room"}</DialogBtn>
              </div>
            </div>
          )}
        </Dialog>

        <Dialog isOpen={!!blocking} toggle={() => !busy && this.setState({ blocking: null })}>
          {blocking && (
            <div className="rs-form">
              <DialogHead
                title={`Take room ${blocking.unit.number} out of service`}
                subtitle="The app stops selling it for these nights. Existing bookings are not moved."
                onClose={() => this.setState({ blocking: null })}
              />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 6 }}>
                <Field label="First night"><Input type="date" value={blocking.from} max={blocking.to || undefined} onChange={(e) => set("blocking")({ from: e.target.value })} /></Field>
                <Field label="Last night" hint="Included."><Input type="date" value={blocking.to} min={blocking.from || undefined} onChange={(e) => set("blocking")({ to: e.target.value })} /></Field>
              </div>
              <div style={{ marginTop: 14 }}>
                <Field label="Reason"><Input value={blocking.reason} maxLength={120} onChange={(e) => set("blocking")({ reason: e.target.value })} placeholder="e.g. Repainting" /></Field>
              </div>
              <div style={{ marginTop: 22, display: "flex", gap: 12 }}>
                <DialogBtn onClick={() => this.setState({ blocking: null })} disabled={busy}>Cancel</DialogBtn>
                <DialogBtn kind="primary" onClick={this.submitBlock} disabled={busy}>{busy ? "Saving…" : "Block nights"}</DialogBtn>
              </div>
            </div>
          )}
        </Dialog>

        <ConfirmDialog
          isOpen={!!removing}
          toggle={() => !busy && this.setState({ removing: null })}
          loading={busy}
          onConfirm={() => this.run(deleteRoomUnit(removing._id), () => this.setState({ removing: null }))}
          title={`Remove room ${removing ? removing.number : ""}?`}
          body="It stops counting toward this branch's stock. Bookings already made are kept."
          confirmText="Remove room"
        />
      </React.Fragment>
    );
  }

  render() {
    const { branches, branchId, isLoaded, units } = this.state;
    const write = canWrite();
    const groups = this.groups();
    const scoped = isBranchScopedAdmin();
    const active = units.filter((u) => u.isActive).length;

    return (
      <ConsolePage
        title="Room stock"
        subtitle={isLoaded ? `${units.length} room${units.length === 1 ? "" : "s"} · ${active} on sale${scoped ? ` · ${getUserBranchName() || "your branch"}` : ""}` : "Loading…"}
        actions={
          <React.Fragment>
            {!scoped && (
              <select
                aria-label="Branch"
                value={branchId}
                onChange={(e) => this.setState({ branchId: e.target.value }, this.load)}
                style={{ height: 42, borderRadius: 21, border: `1px solid ${C.border}`, background: "#fff", padding: "0 14px", fontSize: 14, color: C.brownDeep, fontFamily: "inherit" }}
              >
                {branches.map((b) => <option key={b._id} value={b._id}>{b.name || b.code}</option>)}
              </select>
            )}
            <Btn kind="quiet" icon="calendar_month" onClick={() => this.props.history.push("/rooms/availability")}>Availability</Btn>
            {write && (
              <Btn icon="add" disabled={!this.state.types.length} onClick={() => this.setState({ adding: { roomTypeId: "", numbers: "", floor: "", notes: "" } })}>
                Add rooms
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        {this.renderDialogs()}

        {!isLoaded ? (
          <div style={{ padding: "80px 0", textAlign: "center", color: C.taupeLight }}>Loading rooms…</div>
        ) : !groups.length ? (
          <Panel title="No room types at this branch">
            <div style={{ marginTop: 10, fontSize: 14, color: C.slate }}>
              Make a room type available at this branch first (Rooms → room types), then add its rooms here.
            </div>
          </Panel>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {groups.map((g) => (
              <Panel
                key={g.id}
                title={g.type}
                subtitle={
                  g.units.length
                    ? `${g.units.filter((u) => u.isActive).length} of ${g.units.length} on sale`
                    : "No rooms entered — the app is selling this type's old room count. Add rooms to control availability."
                }
                actions={
                  write && !g.stray && (
                    <Btn kind="quiet" icon="add" onClick={() => this.setState({ adding: { roomTypeId: g.id, numbers: "", floor: "", notes: "" } })}>
                      Add
                    </Btn>
                  )
                }
              >
                {g.units.length > 0 && <div style={{ marginTop: 10 }}>{g.units.map((u) => this.renderUnitRow(u, write))}</div>}
              </Panel>
            ))}
          </div>
        )}
      </ConsolePage>
    );
  }
}

export default RoomUnits;
